import {
  labelOf,
  PROJECT_COST_BASES,
  PROJECT_STATUSES,
  type Project,
  type ProjectSummary,
} from '@facture/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  CalendarRangeIcon,
  CheckCircle2Icon,
  FileDownIcon,
  HardHatIcon,
  MoreHorizontalIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  Trash2Icon,
  UserRoundIcon,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { AnimatedNumber, FadeIn, Stagger, StaggerItem } from '@/components/motion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { confirm } from '@/components/ui/confirm'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tabs, TabsList, TabsTrigger, Tooltip } from '@/components/ui/misc'
import { Page } from '@/components/ui/page'
import { Skeleton } from '@/components/ui/skeleton'
import { weekStartOf } from '@/features/payroll/payroll-ui'
import { PersonalTab } from '@/features/payroll/personal-tab'
import { ScheduleCard } from '@/features/payroll/schedule-card'
import { PROJECT_STATUS_VARIANT, projectQuery, projectSummaryQuery } from '@/features/projects/api'
import { ProjectFormFields, projectDefaults, useProjectForm } from '@/features/projects/project-form'
import { CostsTab, ItemsTab, PhasesTab } from '@/features/projects/project-tabs'
import {
  BudgetMeter,
  CategoryDonut,
  CostWaterfall,
  CumulativeChart,
  pctText,
  RankBars,
} from '@/features/projects/project-visuals'
import { ReportsDialog } from '@/features/projects/reports-dialog'
import { SettlementsTab } from '@/features/settlements/settlements-tab'
import { api, errorMessage } from '@/lib/api'
import { addDaysISO, todayCR } from '@/lib/dates'
import { formatDate, formatMoney } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const TABS = [
  ['summary', 'Resumen'],
  ['costs', 'Costos'],
  ['items', 'Artículos'],
  ['phases', 'Partidas'],
  ['personal', 'Personal'],
  ['settlements', 'Liquidaciones'],
  ['info', 'Ficha'],
] as const
type Tab = (typeof TABS)[number][0]

export const Route = createFileRoute('/_app/projects/$id')({
  staticData: { crumb: 'Proyecto' },
  validateSearch: z.object({
    tab: z.enum(['summary', 'costs', 'items', 'phases', 'personal', 'settlements', 'info']).optional(),
    /** Semana de la hoja de Personal (cualquier fecha de la semana, yyyy-MM-dd). */
    week: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  }),
  component: ProjectPage,
})

function ProjectPage() {
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const canPayroll = useCan('payroll:read')
  const payrollTab = search.tab === 'personal' || search.tab === 'settlements'
  const tab: Tab = payrollTab && !canPayroll ? 'summary' : (search.tab ?? 'summary')
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const canWrite = useCan('projects:write')
  const { data: project, isLoading, error } = useQuery(projectQuery(id))
  const summary = useQuery(projectSummaryQuery(id))
  const [reports, setReports] = useState(false)
  const setStatus = useMutation({
    mutationFn: (status: Project['status']) => {
      if (!project) throw new Error('Sin proyecto')
      return api.put<Project>(`/projects/${id}`, { ...projectDefaults(project), status })
    },
    onSuccess: (p) => {
      toast.success(`Proyecto ${labelOf(PROJECT_STATUSES, p.status).toLowerCase()}`)
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  if (error) {
    return (
      <Page>
        <p className="text-muted-foreground text-sm">{errorMessage(error)}</p>
      </Page>
    )
  }
  if (isLoading || !project) {
    return (
      <Page>
        <Skeleton className="mb-6 h-36 rounded-2xl" />
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-xl" />
      </Page>
    )
  }

  const s = summary.data
  const setTab = (next: Tab) =>
    navigate({
      search: (prev) => ({
        tab: next === 'summary' ? undefined : next,
        week: next === 'personal' ? prev.week : undefined,
      }),
      replace: true,
      resetScroll: false,
    })

  const remove = () =>
    confirm({
      title: project.allocationCount ? '¿Archivar este proyecto?' : '¿Eliminar este proyecto?',
      description: project.allocationCount
        ? 'Tiene costos imputados: se marca como cancelado y conserva su historia. Sus gastos siguen en Facturación.'
        : 'Se eliminan también sus fases y partidas administrativas.',
      confirmLabel: project.allocationCount ? 'Archivar' : 'Eliminar',
      destructive: true,
      onConfirm: async () => {
        const result = await api.delete<{ deleted: boolean }>(`/projects/${id}`)
        queryClient.invalidateQueries({ queryKey: ['projects'] })
        if (result.deleted) {
          toast.success('Proyecto eliminado')
          navigate({ to: '/projects' })
        } else toast.success('Proyecto archivado como cancelado')
      },
    })

  return (
    <Page>
      <div className="relative mb-6 overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-linear-to-br from-warning/14 via-warning/4 to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full bg-warning/12 blur-3xl"
        />
        <div className="relative flex flex-col gap-5 p-5 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <Button asChild variant="ghost" size="icon-sm" className="mt-1 shrink-0">
              <Link to="/projects" aria-label="Volver a proyectos">
                <ArrowLeftIcon />
              </Link>
            </Button>
            <span className="hidden size-14 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br from-warning to-[oklch(0.68_0.17_45)] text-white shadow-elevation-2 ring-4 ring-card sm:flex">
              <HardHatIcon className="size-7" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="line-clamp-2 min-w-0 break-words font-semibold text-xl tracking-tight sm:truncate sm:text-2xl">
                  {project.name}
                </h1>
                {project.code && (
                  <span className="rounded-md border border-border bg-subtle px-1.5 py-0.5 font-mono text-muted-foreground text-xs">
                    {project.code}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={PROJECT_STATUS_VARIANT[project.status]} dot>
                  {labelOf(PROJECT_STATUSES, project.status)}
                </Badge>
                <Badge variant="outline" className="hidden sm:inline-flex">
                  {labelOf(PROJECT_COST_BASES, project.costBasis)}
                </Badge>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-muted-foreground text-sm">
                <span className="flex items-center gap-1.5">
                  <UserRoundIcon className="size-3.5" />
                  {project.customerId ? (
                    <Link
                      to="/customers/$id"
                      params={{ id: project.customerId }}
                      className="hover:text-foreground hover:underline"
                    >
                      {project.customerName}
                    </Link>
                  ) : (
                    'Sin cliente'
                  )}
                </span>
                {(project.startDate || project.endDate) && (
                  <span className="flex items-center gap-1.5">
                    <CalendarRangeIcon className="size-3.5" />
                    {formatDate(project.startDate)} → {formatDate(project.endDate)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="rounded-xl border border-border bg-card/70 px-4 py-2 text-right backdrop-blur max-sm:flex-1">
              <div className="text-muted-foreground text-xs">Gran total</div>
              <div className="font-semibold text-lg tabular">
                {s ? (
                  <AnimatedNumber value={s.grandTotal} format={formatMoney} />
                ) : (
                  <Skeleton className="h-7 w-32" />
                )}
              </div>
            </div>
            <Button variant="outline" onClick={() => setReports(true)}>
              <FileDownIcon /> <span className="max-sm:sr-only">Reportes</span>
            </Button>
            {canWrite && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Más acciones">
                    <MoreHorizontalIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {project.status !== 'active' && (
                    <DropdownMenuItem onSelect={() => setStatus.mutate('active')}>
                      <PlayCircleIcon /> Marcar activo
                    </DropdownMenuItem>
                  )}
                  {project.status === 'active' && (
                    <DropdownMenuItem onSelect={() => setStatus.mutate('paused')}>
                      <PauseCircleIcon /> Poner en pausa
                    </DropdownMenuItem>
                  )}
                  {project.status !== 'completed' && project.status !== 'cancelled' && (
                    <DropdownMenuItem onSelect={() => setStatus.mutate('completed')}>
                      <CheckCircle2Icon /> Marcar terminado
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem destructive onSelect={remove}>
                    <Trash2Icon /> {project.allocationCount ? 'Archivar' : 'Eliminar'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {project.status === 'completed' && project.lockOnComplete && (
        <p className="mb-4 rounded-lg border border-border bg-muted/50 px-3 py-2 text-muted-foreground text-sm">
          Este proyecto está terminado y congelado: no recibe horas, costos ni recálculos. Las liquidaciones
          siguen abiertas. Para registrar de nuevo, pasalo a Activo o apagá el candado en la Ficha.
        </p>
      )}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="mb-6">
        <TabsList aria-label="Secciones del proyecto" className="w-full justify-start sm:w-auto">
          {TABS.filter(([value]) => (value !== 'personal' && value !== 'settlements') || canPayroll).map(
            ([value, label]) => (
              <TabsTrigger key={value} value={value}>
                {label}
              </TabsTrigger>
            ),
          )}
        </TabsList>
      </Tabs>

      {tab === 'summary' &&
        (s ? (
          <SummaryTab project={project} summary={s} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-28 rounded-xl" />
            ))}
          </div>
        ))}
      {tab === 'costs' && <CostsTab projectId={id} />}
      {tab === 'items' && <ItemsTab projectId={id} />}
      {tab === 'phases' && <PhasesTab projectId={id} />}
      {tab === 'personal' && (
        <PersonalTab
          project={project}
          week={search.week ?? todayCR()}
          onWeekChange={(week) =>
            navigate({ search: (prev) => ({ ...prev, week }), replace: true, resetScroll: false })
          }
        />
      )}
      {tab === 'settlements' && <SettlementsTab project={project} />}
      {tab === 'info' && <InfoTab project={project} />}
      <ReportsDialog
        project={project}
        open={reports}
        onClose={() => setReports(false)}
        initialRange={tab === 'personal' ? sheetWeek(search.week) : undefined}
      />
    </Page>
  )
}

/** Semana de la hoja de Personal (lunes a domingo, sin pasar de hoy): el periodo inicial de la planilla y la ficha. */
function sheetWeek(week: string | undefined) {
  const today = todayCR()
  const from = weekStartOf(week && week <= today ? week : today)
  const end = addDaysISO(from, 6)
  return { from, to: end < today ? end : today }
}

/* ------------------------------ Resumen ------------------------------ */

function SummaryTab({ project, summary: s }: { project: Project; summary: ProjectSummary }) {
  const consumed = s.budget.consumedPct
  const over = s.budget.overrun > 0
  return (
    <FadeIn className="grid gap-6">
      <Stagger className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric
          label="Costo real"
          value={s.costReal}
          accent
          hint={consumed == null ? 'Sin presupuesto definido' : `${pctText(consumed)} del presupuesto`}
        />
        <Metric
          label="Presupuesto"
          value={s.budget.amount}
          hint={project.budget ? 'Aprobado para la obra' : 'Definilo en la Ficha'}
        />
        <Metric
          label={over ? 'Sobregiro' : 'Disponible'}
          value={over ? s.budget.overrun : s.budget.remaining}
          tone={over ? 'danger' : s.budget.amount ? 'success' : undefined}
          hint={over ? 'El costo superó el presupuesto' : 'Presupuesto − costo real'}
        />
        <Metric label="Utilidad" value={s.utility} hint={s.adminFee.detail} />
      </Stagger>

      <Card>
        <CardHeader>
          <div>
            <CardTitle>Consumo del presupuesto</CardTitle>
            <p className="mt-1 text-muted-foreground text-sm">Costo real contra lo presupuestado</p>
          </div>
          {consumed != null && (
            <span
              className={cn(
                'font-semibold text-2xl tracking-tight tabular',
                over ? 'text-destructive' : consumed > 85 ? 'text-warning-soft-foreground' : 'text-success',
              )}
            >
              <AnimatedNumber value={consumed} format={pctText} />
            </span>
          )}
        </CardHeader>
        <CardContent className="grid gap-3">
          {s.budget.amount > 0 ? (
            <>
              <BudgetMeter pct={consumed ?? 0} />
              <div className="flex flex-wrap justify-between gap-2 text-muted-foreground text-xs tabular">
                <span>
                  Consumido <strong className="font-medium text-foreground">{formatMoney(s.costReal)}</strong>
                </span>
                <span>
                  {over ? 'Sobregiro ' : 'Disponible '}
                  <strong className={cn('font-medium', over ? 'text-destructive' : 'text-foreground')}>
                    {formatMoney(over ? s.budget.overrun : s.budget.remaining)}
                  </strong>{' '}
                  de {formatMoney(s.budget.amount)}
                </span>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">
              Este proyecto no tiene presupuesto. Agregalo en la pestaña Ficha para medir su consumo.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Del costo al gran total</CardTitle>
              <p className="mt-1 text-muted-foreground text-sm">
                Cada barra suma (o resta) hasta el costo real y el precio final
              </p>
            </div>
          </CardHeader>
          <CardContent className="h-80">
            <CostWaterfall summary={s} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Cascada</CardTitle>
              <p className="mt-1 text-muted-foreground text-sm">
                Calculada en el servidor con una sola fórmula
              </p>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <Cascade summary={s} areaSqm={project.areaSqm} />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Costo acumulado</CardTitle>
              <p className="mt-1 text-muted-foreground text-sm">
                Costo de cada mes y acumulado
                {s.budget.amount > 0 ? ` · presupuesto ${formatMoney(s.budget.amount)}` : ''}
              </p>
            </div>
          </CardHeader>
          <CardContent className="h-64">
            {s.byMonth.length ? (
              <CumulativeChart summary={s} />
            ) : (
              <p className="text-muted-foreground text-sm">Todavía no hay costos imputados.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Por fase</CardTitle>
              <p className="mt-1 text-muted-foreground text-sm">Costo de cada fase contra su presupuesto</p>
            </div>
          </CardHeader>
          <CardContent>
            <RankBars
              empty="Sin fases. Creálas en la pestaña Partidas."
              items={s.byPhase.map((ph) => ({
                key: ph.phaseId ?? 'none',
                label: ph.name,
                value: ph.cost,
                budget: ph.budget || undefined,
                hint: ph.budget
                  ? `${pctText(ph.consumedPct ?? 0)} de ${formatMoney(ph.budget)} · ${countLabel(ph.count)}`
                  : countLabel(ph.count),
              }))}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Por categoría de gasto</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryDonut summary={s} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Por proveedor</CardTitle>
          </CardHeader>
          <CardContent>
            <RankBars
              empty="Sin costos imputados."
              items={s.bySupplier.slice(0, 6).map((sp) => ({
                key: sp.name,
                label: sp.name,
                value: sp.cost,
                hint: countLabel(sp.count),
              }))}
            />
          </CardContent>
        </Card>
      </div>
    </FadeIn>
  )
}

const countLabel = (n: number) => `${n} imputaci${n === 1 ? 'ón' : 'ones'}`

function Metric({
  label,
  value,
  hint,
  accent,
  tone,
}: {
  label: string
  value: number
  hint?: ReactNode
  accent?: boolean
  tone?: 'danger' | 'success'
}) {
  const text = formatMoney(value)
  return (
    <StaggerItem>
      <Card className="relative h-full overflow-hidden p-5">
        {accent && (
          <div className="pointer-events-none absolute -top-16 -right-10 size-44 rounded-full bg-warning/20 blur-3xl" />
        )}
        <div className="relative">
          <div className="text-muted-foreground text-sm">{label}</div>
          <div
            className={cn(
              'mt-2 truncate font-semibold tracking-tight tabular',
              text.length > 16 ? 'text-xl' : 'text-[26px]',
              tone === 'danger' && 'text-destructive',
              tone === 'success' && 'text-success',
            )}
          >
            <AnimatedNumber value={value} format={formatMoney} />
          </div>
          {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
        </div>
      </Card>
    </StaggerItem>
  )
}

/** Los pasos de la fórmula, con los totales resaltados y la identidad costo real + utilidad = gran total. */
function Cascade({ summary: s, areaSqm }: { summary: ProjectSummary; areaSqm?: number | null }) {
  const rows: { label: string; value: number; detail?: string; strong?: boolean; sub?: boolean }[] = [
    { label: 'Facturas recibidas', value: s.invoices.cost, detail: `${s.invoices.count}` },
    ...(s.credits.count
      ? [{ label: 'Notas de crédito', value: s.credits.cost, detail: `${s.credits.count}` }]
      : []),
    { label: 'Gastos', value: s.expenses.cost, detail: `${s.expenses.count}` },
    { label: 'Costo de terceros', value: s.thirdPartyCost, strong: true },
    {
      label: 'Mano de obra',
      value: s.labor.base,
      detail: s.labor.workers ? `${s.labor.workers}` : undefined,
    },
    ...(s.labor.allowances
      ? [{ label: 'de ella, dietas y transporte', value: s.labor.allowances, sub: true }]
      : []),
    {
      label: 'Cargas sociales',
      value: s.labor.charges,
      detail: s.labor.chargesDetail,
    },
    ...s.adminItems.map((i) => ({
      label: i.name,
      value: i.amount,
      detail: i.kind === 'percent' ? pctText(i.value) : undefined,
      sub: true,
    })),
    { label: 'Costo real', value: s.costReal, strong: true },
    {
      label: 'Utilidad de administración',
      value: s.adminFee.amount,
      detail: s.adminFee.detail,
    },
    ...(s.costGroups.applied
      ? [
          ...s.costGroups.rows
            .filter((g) => g.origin === 'compras')
            .map((g) => ({
              label: g.name,
              value: g.admin,
              detail: pctText(g.pct),
              sub: true,
            })),
          ...(s.costGroups.pendingCount
            ? [
                {
                  label: 'Sin clasificar',
                  value: 0,
                  detail: `${s.costGroups.pendingCount}`,
                  sub: true,
                },
              ]
            : []),
        ]
      : []),
    ...(s.labor.margin
      ? [
          {
            label: s.costGroups.applied ? 'Administración de planilla' : 'Margen mano de obra',
            value: s.labor.margin,
            detail: s.labor.marginDetail,
          },
        ]
      : []),
    { label: 'Gran total', value: s.grandTotal, strong: true },
    ...(areaSqm && areaSqm > 0
      ? [{ label: 'Costo por m²', value: s.costReal / areaSqm, detail: `${areaSqm} m²` }]
      : []),
  ]
  return (
    <div className="grid">
      {rows.map((r) => (
        <div
          key={r.label}
          className={cn(
            'flex items-baseline justify-between gap-3 py-1.5 text-sm',
            r.strong && 'mt-1 border-border border-t pt-2.5 font-semibold',
            r.sub && 'pl-4 text-muted-foreground',
          )}
        >
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="truncate">{r.label}</span>
            {r.detail && (
              <Tooltip
                content={
                  r.detail.match(/^\d+$/)
                    ? r.label === 'Mano de obra'
                      ? 'Trabajadores'
                      : 'Imputaciones'
                    : 'Tasa'
                }
              >
                <span className="shrink-0 rounded bg-muted px-1 font-normal text-[11px] text-muted-foreground tabular">
                  {r.detail}
                </span>
              </Tooltip>
            )}
          </span>
          <span className={cn('shrink-0 tabular', r.value < 0 && 'text-destructive')}>
            {formatMoney(r.value)}
          </span>
        </div>
      ))}
      {s.excludedTaxCredit !== 0 && (
        <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-muted-foreground text-xs">
          IVA acreditable excluido del costo: {formatMoney(s.excludedTaxCredit)}
        </p>
      )}
      {s.labor.base > 0 && (
        <p className="mt-2 flex flex-wrap justify-between gap-x-3 gap-y-1 rounded-lg bg-muted px-3 py-2 text-muted-foreground text-xs">
          <span>
            Pagado al personal{' '}
            <strong className="font-medium text-foreground tabular">{formatMoney(s.labor.paid)}</strong>
          </span>
          <span>
            Por pagar{' '}
            <strong className="font-medium text-foreground tabular">{formatMoney(s.labor.pending)}</strong>
            {s.labor.settledThrough ? ` · liquidado hasta el ${formatDate(s.labor.settledThrough)}` : ''}
          </span>
        </p>
      )}
    </div>
  )
}

/* ------------------------------ Ficha ------------------------------ */

function InfoTab({ project }: { project: Project }) {
  const canWrite = useCan('projects:write')
  const canPayroll = useCan('payroll:read')
  const { form, save, submit } = useProjectForm(project)
  return (
    <div className="grid gap-6">
      <Card>
        <form onSubmit={submit} noValidate>
          <CardContent className="p-5 sm:p-6">
            <fieldset disabled={!canWrite} className="contents">
              <ProjectFormFields form={form} customerLabel={project.customerName} />
            </fieldset>
          </CardContent>
          {canWrite && (
            <div className="flex items-center justify-end gap-2 rounded-b-xl border-border border-t bg-subtle px-5 py-3">
              <Button
                type="button"
                variant="ghost"
                disabled={!form.formState.isDirty}
                onClick={() => form.reset()}
              >
                Descartar cambios
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!form.formState.isDirty}>
                Guardar
              </Button>
            </div>
          )}
        </form>
      </Card>
      {canPayroll && <ScheduleCard project={project} canWrite={canWrite} />}
    </div>
  )
}
